import React from 'react';

export interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
    /**
     * Доступное имя кнопки — обязательное. Кнопка без текста (только иконка) без него
     * не имеет имени вообще: скринридер читает «кнопка», а подсказки по наведению нет
     * (QA-16). Уходит и в `aria-label`, и в `title`.
     */
    label: string;
    /** Подсказка, если она должна отличаться от имени (например, длиннее). */
    title?: string;
    /** Иконка. Помечается `aria-hidden` — имя даёт `label`. */
    children: React.ReactNode;
    /**
     * Тач-зона. `touch` (по умолчанию) — 44×44 по styleguide §Accessibility.
     * `compact` — floor WCAG 24×24 и только для кнопок внутри плотных композитов
     * (крестик на чипе фильтра, кнопка поверх миниатюры): там 44px перекрыли бы
     * сам композит. Для отдельно стоящих кнопок `compact` не используем.
     */
    size?: 'touch' | 'compact';
}

const SIZE_CLASS: Record<'touch' | 'compact', string> = {
    touch: 'min-w-[44px] min-h-[44px]',
    compact: 'min-w-[24px] min-h-[24px]',
};

/**
 * Кнопка-иконка без подписи: единственная точка, где задаётся её доступное имя
 * и минимальная тач-зона (styleguide §Accessibility: 44×44 для touch).
 *
 * Внешний вид задаётся через `className` — компонент не навязывает цвета и радиусы,
 * чтобы существующие кнопки перешли на него без визуальных изменений.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    ({label, title, children, className = '', type = 'button', size = 'touch', ...rest}, ref) => (
        <button
            ref={ref}
            type={type}
            aria-label={label}
            title={title ?? label}
            className={`inline-flex items-center justify-center ${SIZE_CLASS[size]} ${className}`}
            {...rest}
        >
            <span aria-hidden="true" className="inline-flex items-center justify-center">
                {children}
            </span>
        </button>
    )
);

IconButton.displayName = 'IconButton';

export default IconButton;
